import { StandardEvents } from '@shopify/events';

const clean = (value) => String(value ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;|&#160;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();
const colorKey = (value) => ({ gold: 'gold', goud: 'gold', silver: 'silver', zilver: 'silver' }[clean(value).toLowerCase()] || clean(value).toLowerCase());
const stripColor = (text) => text.replace(/^(?:gold|goud|silver|zilver)\s*:\s*/i, '');
const labeledColor = (text) => text.match(/^(gold|goud|silver|zilver)\s*:/i)?.[1];
const euSize = (text) => text.match(/\bEU\s*(\d+(?:[.,]\d+)?)/i)?.[1]?.replace(',', '.');

export function sizeLines(description) {
  const section = String(description || '').match(/<h[23][^>]*>\s*(?:size|maat|afmetingen|measurements)\s*<\/h[23]>([\s\S]*?)(?=<h[23]\b|$)/i)?.[1] || '';
  const list = [...section.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi)].map((m) => clean(m[1]));
  if (list.length) return list.filter(Boolean);
  const first = section.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i)?.[1];
  return first ? [clean(first)].filter(Boolean) : [];
}

export function formatSize(value, locale = 'en', supplements = []) {
  let text = stripColor(clean(value));
  const nl = locale.toLowerCase().startsWith('nl');
  const eu = euSize(text);
  if (eu) {
    const candidates = [text, ...supplements.filter((s) => euSize(s) === eu)];
    let diameter;
    let approximate = false;
    for (const candidate of candidates) {
      diameter = candidate.match(/(?:\bdiameter\b|binnendiameter|Ø)\s*(?:(?:approximately|approx\.?|circa|ca\.?)\s*)?(\d+(?:[.,]\d+)?)\s*mm/i)
        || candidate.match(/(\d+(?:[.,]\d+)?)\s*mm(?:\s*(?:inner\s+)?diameter)?/i);
      if (diameter) {
        approximate = /\b(?:approximately|approx\.?|circa|ca\.)/i.test(candidate);
        break;
      }
    }
    const number = (n) => nl ? n.replace('.', ',') : n.replace(',', '.');
    const adjustable = /\b(?:adjustable|verstelbaar|verstelbare)\b/i.test(text);
    return `EU ${number(eu)}${diameter ? ` · Diameter ${approximate ? (nl ? 'ca. ' : 'approx. ') : ''}${number(diameter[1])} mm` : ''}${adjustable ? (nl ? ' · Verstelbaar' : ' · Adjustable') : ''}`;
  }
  const pairs = [
    [/adjustable fit/gi, 'Verstelbaar'], [/adjustable length/gi, 'Verstelbare lengte'], [/inner diameter/gi, 'Binnendiameter'],
    [/elastic fit/gi, 'Elastische pasvorm'], [/adjustable/gi, 'Verstelbaar'], [/length/gi, 'Lengte'],
    [/approximately|approx\./gi, 'Circa'], [/across/gi, 'breed'], [/drop/gi, 'hanger'],
  ];
  const english = [[/verstelbare lengte/gi, 'Adjustable length'], [/binnendiameter/gi, 'Inner diameter'], [/elastische pasvorm/gi, 'Elastic fit'], [/verstelbaar/gi, 'Adjustable'], [/lengte/gi, 'Length'], [/circa/gi, 'Approx.'], [/hanger/gi, 'drop']];
  for (const [pattern, replacement] of nl ? pairs : english) text = text.replace(pattern, replacement);
  return text.replace(/(\d)[.,](\d)/g, (_, a, b) => a + (nl ? ',' : '.') + b).replace(/\s*\+\s*/g, ' + ').trim();
}

export function resolveSize(data, variantId) {
  const variant = data.variants.find((v) => String(v.id) === String(variantId));
  if (!variant) return '';
  const sizeIndex = data.options.findIndex((name) => /^(size|maat)$/i.test(name.trim()));
  const colorIndex = data.options.findIndex((name) => /^(color|colour|kleur)$/i.test(name.trim()));
  const color = colorIndex < 0 ? '' : colorKey(variant.options[colorIndex]);
  const compatible = (text) => !labeledColor(text) || colorKey(labeledColor(text)) === color;
  const lines = sizeLines(data.description);
  const colorLines = lines.filter((line) => labeledColor(line) && compatible(line));
  const genericLines = lines.filter((line) => !labeledColor(line));
  const groups = (data.groups || []).filter((g) => String(g.variantId) === String(variant.id)
    || (colorIndex >= 0 && colorKey(g.options?.[colorIndex]) === color));
  const groupSizes = [...new Set(groups.map((g) => clean(g.size)).filter((s) => s && compatible(s)))];
  const productSize = compatible(clean(data.size)) ? clean(data.size) : '';
  // A selected variant's own size always wins. Never apply a different color's labeled size.
  const optionSize = sizeIndex < 0 ? '' : clean(variant.options[sizeIndex]);
  const specificOption = /^(?:one size|one-size|een maat)$/i.test(optionSize) ? '' : optionSize;
  const distinctGroups = new Set((data.groups || []).map((g) => clean(g.size).match(/\d+(?:[.,]\d+)?/g)?.join('|')).filter(Boolean));
  const numericGroup = distinctGroups.size > 1 && groupSizes.length === 1 && /\d/.test(groupSizes[0]) ? groupSizes[0] : '';
  const detailedGeneric = !/\d/.test(productSize) && genericLines.length === 1 && /\d/.test(genericLines[0]) ? genericLines[0] : '';
  const raw = clean(variant.size) || specificOption
    || colorLines[0] || numericGroup || detailedGeneric || productSize || optionSize || (groupSizes.length === 1 ? groupSizes[0] : '')
    || (genericLines.length === 1 ? genericLines[0] : '');
  return formatSize(raw, data.locale, [...colorLines, ...genericLines, ...groupSizes, productSize]);
}

class ProductSize extends HTMLElement {
  connectedCallback() {
    const source = this.querySelector('[data-size-source]');
    if (!source) return;
    try { this.source = JSON.parse(source.textContent); } catch { return; }
    this.scope = this.closest('.shopify-section');
    this.render(this.source.selectedId);
    this.scope?.addEventListener(StandardEvents.productSelect, this.onSelect);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  render(id) {
    const value = resolveSize(this.source, id);
    this.querySelector('[data-size-value]').textContent = value;
    this.hidden = !value;
    this.dataset.selectedVariant = String(id || '');
  }

  onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const data = event;
    if (data.product?.id && String(data.product.id).split('/').pop() !== this.source.productId) return;
    const options = data.selectedOptions || [];
    const selected = this.source.variants.find((v) => this.source.options.every((name, i) =>
      options.some((o) => o.name === name && o.value === v.options[i])));
    if (options.length) this.render(selected?.id || '');
    const request = event.promise;
    this.pending = request;
    request?.then(({ detail }) => {
      if (this.pending !== request || !this.isConnected) return;
      if (detail?.productId && String(detail.productId) !== this.source.productId) return;
      if (detail?.resource?.id) {
        this.source.selectedId = String(detail.resource.id).split('/').pop();
        this.render(this.source.selectedId);
      }
    }).catch((error) => {
      if (this.pending === request && error?.name !== 'AbortError') this.render(this.source.selectedId);
    });
  };
}

if (!customElements.get('dln-product-size')) customElements.define('dln-product-size', ProductSize);
